import React, { useContext, useEffect, useState } from "react";
import Ctx from "../../contexts/AppContext";
import { api } from "../../api";
import { crd, sp, rad, type, listRow } from "../../constants/styles";
import Ic from "../common/Ic";
import { formatPropAddr } from "../../utils/propertyAddr";

// Status pill colour map — keeps Inbox + Detail in lockstep.
const STATUS_STYLE = {
  initiated:             { label: "Awaiting Submit",    fg: '#94948f', bg: '#94948f15' },
  under_review:          { label: "Needs Your Review",  fg: '#d97706', bg: '#d9770615' },
  full_refund_proposed:  { label: "Awaiting Tenant",    fg: '#3b82f6', bg: '#3b82f615' },
  deduction_proposed:    { label: "Awaiting Tenant",    fg: '#3b82f6', bg: '#3b82f615' },
  agreed:                { label: "Ready to Settle",    fg: '#16a34a', bg: '#16a34a15' },
  disputed:              { label: "Disputed",           fg: '#ef4444', bg: '#ef444415' },
  settled:               { label: "Settled",            fg: '#16a34a', bg: '#16a34a15' },
  cancelled:             { label: "Cancelled",          fg: '#94948f', bg: '#94948f15' },
};

const FILTERS = [
  { id: 'open',   label: 'Open',         test: v => !['settled','cancelled'].includes(v.status) },
  { id: 'review', label: 'Needs Review', test: v => v.status === 'under_review' },
  { id: 'all',    label: 'All',          test: () => true },
];

export default function VacateInbox() {
  const { h, t, go, props, tens, setSelVacateId, setSelVacateRequest, flash } = useContext(Ctx);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.vacateListOrg();
        if (res?.success) setRows(res.vacate_requests || []);
      } catch (e) {
        flash("Failed to load vacate requests", "error");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = rows.filter(FILTERS.find(f => f.id === filter)?.test || (() => true));

  const openDetail = (v) => {
    if (setSelVacateId) setSelVacateId(v.id);
    if (setSelVacateRequest) setSelVacateRequest(v);
    go('vacateDetail');
  };

  const counts = FILTERS.reduce((acc, f) => {
    acc[f.id] = rows.filter(f.test).length;
    return acc;
  }, {});

  if (loading) {
    return <div style={{ paddingTop: 40, textAlign: 'center', color: h.ts }}>Loading vacate inbox…</div>;
  }

  return (
    <div style={{ paddingTop: 16 }}>
      <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>← Back</button>
      <h2 style={{ ...type.h1, color: h.tx, margin: "0 0 12px" }}>Vacate Inbox</h2>

      {/* Filter pills */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {FILTERS.map(f => {
          const active = filter === f.id;
          const n = counts[f.id] || 0;
          return (
            <button key={f.id} onClick={() => setFilter(f.id)} style={{
              padding: "6px 12px",
              borderRadius: 999,
              border: `1px solid ${active ? h.ac : h.bd}`,
              background: active ? `${h.ac}15` : 'transparent',
              color: active ? h.ac : h.ts,
              fontSize: 11, fontWeight: 600, cursor: "pointer",
            }}>
              {f.label} {n > 0 && <span style={{ opacity: 0.6, marginLeft: 4 }}>({n})</span>}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div style={{ ...crd(h), padding: 24, textAlign: "center", color: h.ts }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
          <div style={{ fontSize: 13 }}>No vacate requests in this filter.</div>
        </div>
      ) : (
        <div data-coach="vacate-list" style={{ ...crd(h), padding: 0, overflow: "hidden" }}>
          {filtered.map((v, i) => {
            const tenant = tens.find(x => x.id === v.tenant_id);
            const prop = props.find(p => p.id === v.property_id);
            const st = STATUS_STYLE[v.status] || { label: v.status, fg: h.ts, bg: '#0001' };
            return (
              <div key={v.id} onClick={() => openDetail(v)} style={{
                ...listRow(h, i === filtered.length - 1),
                cursor: "pointer",
                padding: 14,
                alignItems: "stretch",
                flexDirection: "column",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <div style={{ ...type.sm, color: h.tx, fontWeight: 700 }}>
                    {tenant?.name || v.tenant_id}
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
                    padding: "3px 10px", borderRadius: 999,
                    background: st.bg, color: st.fg,
                  }}>{st.label}</span>
                </div>
                <div style={{ color: h.sc, fontSize: 11 }}>
                  {prop ? formatPropAddr(prop) : v.property_id}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 10, color: h.tt }}>
                  <span>Initiated by {v.initiated_by_role}</span>
                  <span>{new Date(v.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
